import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';
import { CompanyConfig, COMPANIES, CompanyId } from '../Config/CompanyConfig';

export interface TenantMismatchData {
  isOpen: boolean;
  reason: string;
  userCompany: CompanyId | 'unknown';
  attemptedCompany: CompanyConfig;
  account?: any;
}

interface TenantAccessDeniedModalProps {
  data: TenantMismatchData | null;
  onClose: () => void | Promise<void>;
  onSignOut?: () => void | Promise<void>;
}

export const TenantAccessDeniedModal: React.FC<TenantAccessDeniedModalProps> = ({
  data,
  onClose,
  onSignOut,
}) => {
  const bg = useColorModeValue('white', '#0F172A');
  const borderColor = useColorModeValue('rgba(239, 68, 68, 0.2)', 'rgba(239, 68, 68, 0.3)');
  const titleColor = useColorModeValue('#0F172A', '#F8FAFC');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const alertBoxBg = useColorModeValue('red.50', 'rgba(239, 68, 68, 0.1)');
  const alertBoxBorder = useColorModeValue('red.100', 'rgba(239, 68, 68, 0.2)');
  const detailsBg = useColorModeValue('gray.50', 'whiteAlpha.050');
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  if (!data || !data.isOpen) return null;

  const targetCompany =
    data.userCompany !== 'unknown' && COMPANIES[data.userCompany]
      ? COMPANIES[data.userCompany]
      : null;

  const handleUnderstood = async () => {
    setIsSigningOut(true);
    try {
      if (onSignOut) {
        await onSignOut();
      } else {
        await onClose();
      }
    } catch (e) {
      console.error("Sign-out on dismissal error:", e);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <Modal isOpen={data.isOpen} onClose={handleUnderstood} isCentered size="md">
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.700" />
      <ModalContent
        bg={bg}
        borderRadius="20px"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.35)"
        overflow="hidden"
        p={2}
      >
        <ModalHeader pt={6} pb={2}>
          <HStack spacing={3}>
            <Box
              p={3}
              borderRadius="12px"
              bg="red.500"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0 4px 12px rgba(239, 68, 68, 0.3)"
            >
              <Icon as={FiAlertTriangle as any} boxSize={5} />
            </Box>
            <Box>
              <Text fontSize="18px" fontWeight={800} color={titleColor} letterSpacing="-0.02em">
                Organization Portal Restriction
              </Text>
              <Text fontSize="12px" color="red.500" fontWeight={600}>
                Security Policy Enforcement
              </Text>
            </Box>
          </HStack>
        </ModalHeader>

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <Box
              p={3.5}
              borderRadius="12px"
              bg={alertBoxBg}
              border="1px solid"
              borderColor={alertBoxBorder}
            >
              <Text fontSize="13px" color={textColor} lineHeight={1.6}>
                {data.reason}
              </Text>
            </Box>

            <Box p={3} borderRadius="12px" bg={detailsBg}>
              <VStack spacing={2} align="stretch" fontSize="12px">
                <HStack justify="space-between">
                  <Text color="gray.500">Attempted Portal:</Text>
                  <Badge colorScheme="blue" borderRadius="full" px={2.5}>
                    {data.attemptedCompany.shortName}
                  </Badge>
                </HStack>
                {targetCompany && (
                  <HStack justify="space-between">
                    <Text color="gray.500">Your Organization:</Text>
                    <Badge colorScheme="purple" borderRadius="full" px={2.5}>
                      {targetCompany.shortName}
                    </Badge>
                  </HStack>
                )}
              </VStack>
            </Box>

            <Text fontSize="12px" color="gray.500" textAlign="center">
              Each subsidiary maintains isolated digital card management. You have been safely signed out.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter pb={4} pt={2}>
          <Button
            w="full"
            h="46px"
            borderRadius="12px"
            bg="red.500"
            color="white"
            _hover={{ bg: 'red.600', transform: 'translateY(-1px)' }}
            _active={{ transform: 'translateY(0)' }}
            transition="all 0.2s"
            fontWeight={600}
            fontSize="14px"
            isLoading={isSigningOut}
            loadingText="Signing out…"
            onClick={handleUnderstood}
          >
            Understood
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TenantAccessDeniedModal;
